import React, { FC } from 'react';

import { RoverLoading } from '@rover/react-lib/src/components';
import { captureMessage } from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { getPath } from '@rover/shared/js/utils/getFullUrl';
import { Http500Error } from '@rover/utilities/HttpError';

import GroomingSeoLocationPageTemplate from '../../templates/GroomingSeoLocationPageTemplate/GroomingSeoLocationPageTemplate';
import { SeoLocationContainerProps, SeoLocationEngineContainer } from '../../types';
import useSeoLocationPageData from '../../utils/useSeoLocationPageData';

const GroomingSeoLocationContainer: FC<SeoLocationContainerProps> = ({
  seoLocation,
  seoLocationDescriptor,
}) => {
  // The placeholder template reads none of this yet, but the page chrome and the sections
  // it grows will, and the queries are already shared with every other engine.
  const { isLoading, isError, queryTags } = useSeoLocationPageData(seoLocationDescriptor);

  if (isError) {
    captureMessage('Unhandled error loading SeoLocationPage', {
      tags: { pathname: getPath(), ...queryTags },
    });
    throw new Http500Error();
  }

  if (isLoading) {
    if (process.env.JS_ENV_CLIENT) {
      // We shouldn't ever show a loading screen in the browser
      captureMessage('SeoLocationPageContainer rendered with missing prefetches', {
        tags: { pathname: getPath(), ...queryTags },
      });
    }

    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  return <GroomingSeoLocationPageTemplate seoLocation={seoLocation} />;
};

// Grooming needs nothing beyond the shared prefetch. Its own queries land with the real
// template.
const reactQueryPrefetch: SeoLocationEngineContainer['reactQueryPrefetch'] = () =>
  Promise.resolve();

export default Object.assign(GroomingSeoLocationContainer, { reactQueryPrefetch });
